/**
 * Analytics tracking helpers.
 *
 * Tagging is handled by a single Google Analytics 4 property loaded directly
 * via gtag.js in index.html.
 *
 *   Measurement ID: G-J67R9N4BCP
 *
 * The Google Ads conversion tag AW-16766879124 is loaded alongside it from the
 * same gtag.js snippet. It is the tag already live on the medispa.hr landing
 * page and is connected to the same Wix booking form, so conversions recorded
 * here land in the existing Ads conversion action.
 *
 * The previous Google Tag Manager container (GTM-KJS6CNJ) has been removed so
 * there is exactly one tracking system on the page and no duplicated hits.
 *
 * PRIVACY: never pass personally identifiable information (name, phone, email,
 * free-text message) into any event below. GA4 rejects PII and it would breach
 * the consent we collect on the lead form. Only categorical values are sent.
 */

export const GA4_MEASUREMENT_ID = 'G-J67R9N4BCP'
export const GOOGLE_ADS_ID = 'AW-16766879124'

export const BOOKING_URL = 'https://medispahr.wixforms.com/f/7479898417246766083'
export const BOOKING_DOMAIN = 'medispahr.wixforms.com'

/** Identifies which section a booking CTA was clicked from. */
export type CtaLocation =
  | 'header'
  | 'hero'
  | 'why_choose'
  | 'what_happens'
  | 'pricing'
  | 'final_cta'
  | 'sticky_nav'
  | 'contact_form'

/** Stable identifiers for the on-page lead form, reused as GA4 event params. */
export const LEAD_FORM_ID = 'contact_form'
export const LEAD_FORM_NAME = 'laser_consultation_lead'

/** Treatment area keys accepted by the submitLead Deno Function. */
export type LeadTreatmentArea =
  | 'lice'
  | 'intimne_manje_zone'
  | 'tijelo'
  | 'nisam_siguran'

/** Preferred contact channel keys accepted by the submitLead Deno Function. */
export type LeadPreferredContact = 'telefon' | 'email' | 'whatsapp'

/** Why a lead submission failed, mapped from the function response. */
export type LeadFormErrorReason =
  | 'validation_failed'
  | 'server_error'
  | 'network_error'

type GtagFn = (...args: unknown[]) => void

function getGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { gtag?: GtagFn }
  return typeof w.gtag === 'function' ? w.gtag : null
}

/**
 * GA4 caps string parameter values at 100 characters and silently drops
 * anything longer, so trim defensively before sending.
 */
function truncate(value: string, max = 100): string {
  return value.length > max ? value.slice(0, max) : value
}

/**
 * Single send path for every event. Safe to call before gtag.js has finished
 * loading: the snippet in index.html defines `gtag` synchronously and queues
 * calls on the dataLayer.
 */
function sendEvent(name: string, params: Record<string, unknown>): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', name, params)
}

/**
 * Sends a `book_now_click` event to GA4 when a booking CTA is clicked.
 */
export function trackBookingClick(location: CtaLocation): void {
  sendEvent('book_now_click', {
    cta_location: location,
    link_domain: BOOKING_DOMAIN,
    link_url: BOOKING_URL
  })
}

/**
 * Fires once per page view, the first time the visitor interacts with any field
 * of the lead form. Pair with `generate_lead` in GA4 to measure form drop-off.
 */
export function trackLeadFormStart(): void {
  sendEvent('form_start', {
    form_id: LEAD_FORM_ID,
    form_name: LEAD_FORM_NAME,
    cta_location: 'contact_form'
  })
}

export interface LeadFormSubmitParams {
  /** Treatment area chosen, if the visitor selected one. */
  treatmentArea?: LeadTreatmentArea
  /** Preferred contact channel, if the visitor selected one. */
  preferredContact?: LeadPreferredContact
  /** Site language at submission time. */
  language?: 'hr' | 'en'
  /** Whether the optional free-text message was filled in. */
  hasMessage?: boolean
  /** Whether the optional email field was filled in. */
  hasEmail?: boolean
}

/**
 * Sends the GA4 recommended `generate_lead` event after the submitLead function
 * confirms the lead was stored. This is the event to mark as a key event in
 * GA4 Admin and to import as a Google Ads conversion, because unlike
 * `book_now_click` it proves a real, verified lead rather than an outbound
 * click to the Wix form.
 */
export function trackLeadFormSubmit(params: LeadFormSubmitParams = {}): void {
  sendEvent('generate_lead', {
    form_id: LEAD_FORM_ID,
    form_name: LEAD_FORM_NAME,
    cta_location: 'contact_form',
    treatment_area: params.treatmentArea ?? 'not_provided',
    preferred_contact: params.preferredContact ?? 'not_provided',
    page_language: params.language ?? 'hr',
    has_message: params.hasMessage === true,
    has_email: params.hasEmail === true
  })
}

/**
 * Records a failed lead submission so drop-off caused by validation problems is
 * visible in GA4 rather than looking like abandonment.
 *
 * @param reason Coarse failure category.
 * @param fields Field names that failed validation. Names only, never values.
 */
export function trackLeadFormError(
  reason: LeadFormErrorReason,
  fields: string[] = []
): void {
  sendEvent('form_error', {
    form_id: LEAD_FORM_ID,
    form_name: LEAD_FORM_NAME,
    cta_location: 'contact_form',
    error_reason: reason,
    error_fields: fields.length > 0 ? truncate(fields.join(',')) : 'none'
  })
}
